/**
 * ANNOTATE MODE for the preview stage — the /artifact-agent "agentation" experience
 * (`DeckGen/artifactAgent/ArtifactAnnotateLayer` + the Agentation SDK it mounted), ported as one
 * host-agnostic layer that rides `annotateBus` instead of the SDK's per-URL localStorage.
 *
 * ## What is ported, and from where
 *
 * The SDK could not come along as a dependency here — the source drove it through measured hacks
 * (an off-screen toolbar poked by synthetic keydowns, a 300ms re-arm watchdog, page-wide fence
 * stamps, a localStorage scoping shim) precisely because it owns page-global state this pane must
 * not own. So the *experience* is ported instead, from the SDK's own sources (agentation@3.0.2,
 * recovered via its sourcemap): the 22px numbered markers with their enter/exit springs, the green
 * 26px rounded square for area notes, the hover tooltip, the 280px "What should change?" composer
 * with its pop-in/shake/flip-above behavior, the accent/green/red palette, the drag-select rect,
 * the hover highlight, and the element identification that names what a note points at. Visual
 * values are the SDK's verbatim; only the class names are ours.
 *
 * ## Deviations from the source, and why (each one forced by the preview's architecture)
 *
 * 1. **No shadow-tree re-render (`annotateShadowDoc` not applicable).** The source swapped its
 *    sandboxed iframe for a DOMPurify'd shadow-root copy so the SDK could run element pickers in
 *    the page. The preview's `ArtifactDocument` ALREADY renders the baked artifact as a page-level
 *    shadow root (sanitized at the source, fonts registered) — re-rendering it would duplicate the
 *    stage under the host's feet, and this layer owns only itself. So annotations sit ON the stage,
 *    measured from OUTSIDE: clicks land in the artifact's own 1920x1080 coordinates, and element
 *    identity is read by geometric hit-test into that existing shadow root when one is reachable.
 *    Over the live (unbaked) iframe the origin is opaque — notes still place, carrying coordinates
 *    instead of element names, and the payload says exactly where they point.
 * 2. **Coordinates, not selectors, are the anchor.** The bus stores artifact px (see `annotateBus`);
 *    the outgoing markdown carries a `**Position:**` line per note so the generator can act even on
 *    a note with no element identity. Element name/path ride along whenever the hit-test found one.
 * 3. **Chrome counter-scales; geometry does not.** The layer lives INSIDE the scaled frame
 *    (`ArtifactFrame`'s `.canvas`, authored at natural size — the same trick as the source pane's
 *    scaled iframe), so a 22px marker would paint as a ~9px dot at Fit — the exact bug the source
 *    fixed twice. Its own remedy is ported: markers/tooltip/popup carry `scale(var(--d15a-zoom))`
 *    where zoom = clamp(1, 1/scale, 3) (the source's `__artifactAnnotateZoom` contract, same
 *    clamp), composed around anchors so nothing drifts; highlight/drag rects hug artifact geometry
 *    and only their border weight counter-scales.
 * 4. **Layout (drag-to-move) mode is not ported.** The SDK's layout mode dragged live DOM elements;
 *    over a non-interactive stage there is nothing to drag. `annotationsToPrompt` keeps the source's
 *    layout-changes appendix branch anyway, so an SDK-shaped payload still behaves identically.
 * 5. **Done unmounts everything, like the source.** When `active` is false the layer renders
 *    nothing — the source's Done chip unmounted its layer the same way; the notes stay on the bus
 *    for the chat side.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { type AnnotationKey,
  addAnnotation,
  annotationEditRequest,
  annotationsServerVersion,
  annotationsVersion,
  focusAnnotation,
  focusedAnnotationId,
  listAnnotations,
  removeAnnotation,
  subscribeAnnotations,
  updateAnnotation,
  type SlideAnnotation,
} from './annotateBus';
import { annotationName } from './withAnnotations';
import { hitTestArtifact, type ArtifactHitDescriptor } from '../preview/artifactFrameBus';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface AnnotateLayerProps {
  /** Which document's notes to draw and write — keys the bus. A deck artifact is a number, a kit composition
   *  is its role id; the bus takes either. */
  slideId: AnnotationKey;
  /** The natural coordinate space of the box this layer overlays (1920x1080 for a slide). */
  width: number;
  height: number;
  /** Edit mode on: clicks place notes, drags select areas, markers edit. Off: render nothing —
   *  the source's Done unmounted its layer the same way; the notes stay staged on the bus. */
  active: boolean;
  /** Chrome theme for the composer and tooltips — the SDK's dark look is the source default. */
  theme?: 'dark' | 'light';
}

/* ── the SDK's stylesheet, ported ───────────────────────────────────────────────────────────────
 *
 * One document-level tag, injected the way the SDK injected its own (`feedback-tool-styles-*`):
 * created on first mount, text refreshed if stale (HMR-safe). Values are agentation@3.0.2's own —
 * marker/tooltip from annotation-marker/styles.module.scss, composer from annotation-popup-css,
 * highlight/drag from the toolbar stylesheet, palette from COLOR_OPTIONS (accent = "blue", the
 * SDK default) — with `--d15a-zoom` composed into every transform that must read at screen size
 * (deviation 3 above). Our own var names, so a page that ever mounts the real SDK next to this
 * layer (the /artifact-agent route lives in the same bundle) cannot cross-style it. */

const STYLE_ID = 'deck15-annotate-styles';

const ANNOTATE_CSS = `
.d15a-layer{position:absolute;left:0;top:0;z-index:10;cursor:crosshair;user-select:none;text-align:left;
  font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  --d15a-blue:#0088FF;--d15a-green:#34C759;--d15a-red:#FF383C;--d15a-accent:var(--d15a-blue);}
@supports (color: color(display-p3 0 0 1)){.d15a-layer{--d15a-blue:color(display-p3 0.00 0.53 1.00);--d15a-green:color(display-p3 0.20 0.78 0.35);--d15a-red:color(display-p3 1.00 0.22 0.24);}}

@keyframes d15aHoverIn{from{opacity:0;transform:scale(0.98);}to{opacity:1;transform:scale(1);}}
.d15a-hover{position:absolute;border:calc(2px * var(--d15a-zoom,1)) solid color-mix(in srgb, var(--d15a-accent) 50%, transparent);
  border-radius:calc(4px * var(--d15a-zoom,1));background:color-mix(in srgb, var(--d15a-accent) 4%, transparent);
  pointer-events:none;box-sizing:border-box;animation:d15aHoverIn 0.12s ease-out forwards;}
.d15a-drag{position:absolute;border:calc(2px * var(--d15a-zoom,1)) solid color-mix(in srgb, var(--d15a-green) 60%, transparent);
  border-radius:calc(4px * var(--d15a-zoom,1));background:color-mix(in srgb, var(--d15a-green) 8%, transparent);
  pointer-events:none;box-sizing:border-box;}
.d15a-region{position:absolute;border:calc(2px * var(--d15a-zoom,1)) dashed color-mix(in srgb, var(--d15a-green) 60%, transparent);
  border-radius:calc(4px * var(--d15a-zoom,1));background:color-mix(in srgb, var(--d15a-green) 5%, transparent);
  pointer-events:none;box-sizing:border-box;}

@keyframes d15aMarkerIn{0%{opacity:0;transform:translate(-50%,-50%) scale(calc(var(--d15a-zoom,1) * 0.3));}
  100%{opacity:1;transform:translate(-50%,-50%) scale(var(--d15a-zoom,1));}}
@keyframes d15aMarkerOut{0%{opacity:1;transform:translate(-50%,-50%) scale(var(--d15a-zoom,1));}
  100%{opacity:0;transform:translate(-50%,-50%) scale(calc(var(--d15a-zoom,1) * 0.3));}}
.d15a-marker{position:absolute;width:22px;height:22px;background:var(--d15a-accent);color:#fff;border-radius:50%;
  display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:600;
  transform:translate(-50%,-50%) scale(var(--d15a-zoom,1));cursor:pointer;
  box-shadow:0 2px 6px rgba(0,0,0,0.2), inset 0 0 0 1px rgba(0,0,0,0.04);user-select:none;z-index:2;
  transition:background-color 0.15s ease, transform 0.1s ease;}
.d15a-marker:hover{z-index:3;transform:translate(-50%,-50%) scale(calc(var(--d15a-zoom,1) * 1.1));}
.d15a-marker.d15a-enter{animation:d15aMarkerIn 0.25s cubic-bezier(0.22,1,0.36,1) both;}
.d15a-marker.d15a-exit{animation:d15aMarkerOut 0.2s ease-out both;pointer-events:none;}
.d15a-marker.d15a-area{width:26px;height:26px;border-radius:6px;font-size:12px;background:var(--d15a-green);}
.d15a-marker.d15a-del{background:var(--d15a-red);}
.d15a-marker.d15a-pending{cursor:default;}
/* PINS WITHOUT THE PEN. Notes stay staged after annotate mode is switched off — they ride the next
   message either way — so the marks stay on the artifact to say so. The layer stops being a surface
   (the artifact underneath is live and its own buttons must keep working) while the marks themselves
   stay reachable, which is what lets a pin still show its note and still open for editing. */
.d15a-layer.d15a-idle{cursor:default;pointer-events:none;}
.d15a-idle .d15a-marker,.d15a-idle .d15a-popup{pointer-events:auto;}
/* Lit from the OTHER end: the pointer is on this note's chip in the composer, not on the mark. Grows
   further than hover's 1.1 because it has to be findable rather than merely acknowledged — the eye is
   in the agent column and has to be drawn across the pane. After the hover rule, which it ties with on
   specificity, so the later declaration wins rather than the bundler's emit order deciding. */
.d15a-marker.d15a-lit{z-index:4;transform:translate(-50%,-50%) scale(calc(var(--d15a-zoom,1) * 1.15));}

@keyframes d15aTipIn{from{opacity:0;transform:translateX(-50%) translateY(2px) scale(0.891);}
  to{opacity:1;transform:translateX(-50%) translateY(0) scale(0.909);}}
.d15a-tip{position:absolute;top:calc(100% + 10px);left:50%;transform:translateX(-50%) scale(0.909);z-index:6;
  background:#1a1a1a;padding:8px 12px;border-radius:12px;font-weight:400;color:#fff;
  box-shadow:0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.08);min-width:120px;max-width:200px;
  pointer-events:none;cursor:default;animation:d15aTipIn 0.1s ease-out forwards;}
.d15a-tip-quote{display:block;font-size:12px;font-style:italic;color:rgba(255,255,255,0.6);margin-bottom:5px;
  line-height:1.4;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.d15a-tip-note{display:block;font-size:13px;font-weight:400;line-height:1.4;color:#fff;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;padding-bottom:2px;}
.d15a-light .d15a-tip{background:#fff;box-shadow:0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06);}
.d15a-light .d15a-tip-quote{color:rgba(0,0,0,0.5);}
.d15a-light .d15a-tip-note{color:rgba(0,0,0,0.85);}

@keyframes d15aPopIn{from{opacity:0;transform:translateX(-50%) scale(calc(var(--d15a-zoom,1) * 0.95)) translateY(4px);}
  to{opacity:1;transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0);}}
@keyframes d15aPopOut{from{opacity:1;transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0);}
  to{opacity:0;transform:translateX(-50%) scale(calc(var(--d15a-zoom,1) * 0.95)) translateY(4px);}}
@keyframes d15aShake{0%,100%{transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0) translateX(0);}
  20%{transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0) translateX(-3px);}
  40%{transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0) translateX(3px);}
  60%{transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0) translateX(-2px);}
  80%{transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0) translateX(2px);}}
.d15a-popup{position:absolute;transform:translateX(-50%) scale(var(--d15a-zoom,1));transform-origin:top center;
  width:280px;padding:12px 16px 14px;background:#1a1a1a;border-radius:16px;box-sizing:border-box;
  box-shadow:0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.08);z-index:7;opacity:0;
  will-change:transform,opacity;cursor:auto;user-select:text;}
.d15a-popup.d15a-up{transform-origin:bottom center;}
.d15a-popup.d15a-pop-enter{animation:d15aPopIn 0.2s cubic-bezier(0.34,1.56,0.64,1) forwards;}
.d15a-popup.d15a-pop-entered{opacity:1;transform:translateX(-50%) scale(var(--d15a-zoom,1)) translateY(0);}
.d15a-popup.d15a-pop-exit{animation:d15aPopOut 0.15s ease-in forwards;}
.d15a-popup.d15a-pop-entered.d15a-pop-shake{animation:d15aShake 0.25s ease-out;}
.d15a-pop-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:9px;}
.d15a-pop-element{font-size:12px;font-weight:400;color:rgba(255,255,255,0.5);max-width:100%;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap;flex:1;}
.d15a-pop-textarea{box-sizing:border-box;width:100%;padding:8px 10px;font-size:13px;font-family:inherit;
  background:rgba(255,255,255,0.05);color:#fff;border:1px solid rgba(255,255,255,0.15);border-radius:8px;
  resize:none;outline:none;transition:border-color 0.15s ease;cursor:text;}
.d15a-pop-textarea::placeholder{color:rgba(255,255,255,0.35);}
.d15a-pop-actions{display:flex;justify-content:flex-end;gap:6px;margin-top:8px;}
.d15a-pop-cancel,.d15a-pop-submit{padding:6px 14px;font-size:12px;font-weight:500;border-radius:16px;border:none;
  cursor:pointer;font-family:inherit;transition:background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;}
.d15a-pop-cancel{background:transparent;color:rgba(255,255,255,0.5);}
.d15a-pop-cancel:hover{background:rgba(255,255,255,0.1);color:rgba(255,255,255,0.8);}
.d15a-pop-submit{color:#fff;}
.d15a-pop-submit:hover:not(:disabled){filter:brightness(0.9);}
.d15a-pop-submit:disabled{cursor:not-allowed;}
.d15a-pop-delete-wrap{margin-right:auto;}
.d15a-pop-delete{cursor:pointer;display:flex;align-items:center;justify-content:center;width:28px;height:28px;
  border-radius:50%;border:none;background:transparent;color:rgba(255,255,255,0.4);padding:0;
  transition:background-color 0.15s ease, color 0.15s ease, transform 0.1s ease;}
.d15a-pop-delete:hover{background-color:color-mix(in srgb, var(--d15a-red) 25%, transparent);color:var(--d15a-red);}
.d15a-pop-delete:active{transform:scale(0.92);}
.d15a-light .d15a-popup{background:#fff;box-shadow:0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06);}
.d15a-light .d15a-pop-element{color:rgba(0,0,0,0.6);}
.d15a-light .d15a-pop-textarea{background:rgba(0,0,0,0.03);color:#1a1a1a;border-color:rgba(0,0,0,0.12);}
.d15a-light .d15a-pop-textarea::placeholder{color:rgba(0,0,0,0.4);}
.d15a-light .d15a-pop-cancel{color:rgba(0,0,0,0.5);}
.d15a-light .d15a-pop-cancel:hover{background:rgba(0,0,0,0.06);color:rgba(0,0,0,0.75);}
.d15a-light .d15a-pop-delete{color:rgba(0,0,0,0.4);}
`;

function ensureAnnotateStyles(): void {
  if (typeof document === 'undefined') return;
  let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_ID;
    document.head.appendChild(tag);
  }
  // Refresh-if-stale rather than write-once — the SDK's own HMR-safe injection contract.
  if (tag.textContent !== ANNOTATE_CSS) tag.textContent = ANNOTATE_CSS;
}

/* ── the SDK's icons (verbatim paths) ──────────────────────────────────────────────────────────── */

function IconPlus({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function IconXmark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M16.25 16.25L7.75 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.75 16.25L16.25 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEdit({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path
        d="M11.3799 6.9572L9.05645 4.63375M11.3799 6.9572L6.74949 11.5699C6.61925 11.6996 6.45577 11.791 6.277 11.8339L4.29549 12.3092C3.93194 12.3964 3.60478 12.0683 3.69297 11.705L4.16585 9.75693C4.20893 9.57947 4.29978 9.4172 4.42854 9.28771L9.05645 4.63375M11.3799 6.9572L12.3455 5.98759C12.9839 5.34655 12.9839 4.31002 12.3455 3.66897C11.7033 3.02415 10.6594 3.02415 10.0172 3.66897L9.06126 4.62892L9.05645 4.63375"
        stroke="currentColor"
        strokeWidth="0.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M13.5 4C14.7426 4 15.75 5.00736 15.75 6.25V7H18.5C18.9142 7 19.25 7.33579 19.25 7.75C19.25 8.16421 18.9142 8.5 18.5 8.5H17.9678L17.6328 16.2217C17.61 16.7475 17.5912 17.1861 17.5469 17.543C17.5015 17.9087 17.4225 18.2506 17.2461 18.5723C16.9747 19.0671 16.5579 19.4671 16.0518 19.7168C15.7227 19.8791 15.3772 19.9422 15.0098 19.9717C14.6514 20.0004 14.2126 20 13.6865 20H10.3135C9.78735 20 9.34856 20.0004 8.99023 19.9717C8.62278 19.9422 8.27729 19.8791 7.94824 19.7168C7.44205 19.4671 7.02532 19.0671 6.75391 18.5723C6.57751 18.2506 6.49853 17.9087 6.45312 17.543C6.40883 17.1861 6.39005 16.7475 6.36719 16.2217L6.03223 8.5H5.5C5.08579 8.5 4.75 8.16421 4.75 7.75C4.75 7.33579 5.08579 7 5.5 7H8.25V6.25C8.25 5.00736 9.25736 4 10.5 4H13.5ZM7.86621 16.1562C7.89013 16.7063 7.90624 17.0751 7.94141 17.3584C7.97545 17.6326 8.02151 17.7644 8.06934 17.8516C8.19271 18.0763 8.38239 18.2577 8.6123 18.3711C8.70153 18.4151 8.83504 18.4545 9.11035 18.4766C9.39482 18.4994 9.76335 18.5 10.3135 18.5H13.6865C14.2367 18.5 14.6052 18.4994 14.8896 18.4766C15.165 18.4545 15.2985 18.4151 15.3877 18.3711C15.6176 18.2577 15.8073 18.0763 15.9307 17.8516C15.9785 17.7644 16.0245 17.6326 16.0586 17.3584C16.0938 17.0751 16.1099 16.7063 16.1338 16.1562L16.4668 8.5H7.5332L7.86621 16.1562ZM9.97656 10.75C10.3906 10.7371 10.7371 11.0626 10.75 11.4766L10.875 15.4766C10.8879 15.8906 10.5624 16.2371 10.1484 16.25C9.73443 16.2629 9.38794 15.9374 9.375 15.5234L9.25 11.5234C9.23706 11.1094 9.56255 10.7629 9.97656 10.75ZM14.0244 10.75C14.4383 10.7635 14.7635 11.1105 14.75 11.5244L14.6201 15.5244C14.6066 15.9384 14.2596 16.2634 13.8457 16.25C13.4317 16.2365 13.1067 15.8896 13.1201 15.4756L13.251 11.4756C13.2645 11.0617 13.6105 10.7366 14.0244 10.75ZM10.5 5.5C10.0858 5.5 9.75 5.83579 9.75 6.25V7H14.25V6.25C14.25 5.83579 13.9142 5.5 13.5 5.5H10.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ── naming what a note points at (deviation 1, rebuilt) ────────────────────────────────────────
 *
 * This layer used to hit-test a shadow root: `ArtifactDocument` rendered settled artifacts as a
 * page-level shadow tree, an open root is readable from the page, and a geometric walk over it named
 * what the pointer was on. That path is GONE — the preview now always mounts the live document in a
 * sandboxed iframe with no `allow-same-origin`, so there is no reachable DOM out here at all and every
 * note had quietly degraded to `point at (x, y)`.
 *
 * The identity comes from inside the frame now. The bridge answers `__bds_hit` with the element under a
 * point plus a three-deep descriptor chain (see `artifactFrameBus`), and the naming stays out here in
 * TypeScript where it is readable and testable — the same rules the SDK's `identifyElement` applied,
 * reading facts off a plain object instead of off an `Element`. Three generations is exactly as far as
 * the deepest rule reaches: an icon inside a button.
 */

/** The SDK's "meaningful class" heuristic, verbatim: skip one- and two-letter utilities and hashed
 *  CSS-module names, keep the human-authored word. */
function meaningfulClass(cls: string): string | undefined {
  return cls
    .split(/\s+/)
    .find((c) => c.length > 2 && !c.match(/^[a-z]{1,2}$/) && !c.match(/[A-Z0-9]{5,}/));
}

/** How one element prints inside a path: `#id`, `.class`, or the bare tag. */
function segmentOf(d: ArtifactHitDescriptor): string {
  if (d.id) return `#${d.id}`;
  const cls = d.cls ? meaningfulClass(d.cls) : undefined;
  return cls ? `.${cls.split('_')[0]}` : d.tag;
}

/**
 * A readable path (`.stage > .stats > h2`) — the SDK's `getElementPath`, off the chain.
 *
 * Three deep where the original walked four, because that is what the bridge sends and a fourth
 * ancestor is a fact nobody has ever read: the path is a hint for the generator, not an address. The
 * real address is the `path` of child indices that rides beside it.
 */
function pathFromChain(chain: readonly ArtifactHitDescriptor[]): string {
  return chain
    .slice()
    .reverse()
    .map(segmentOf)
    .join(' > ');
}

/**
 * The SDK's `identifyElement`, ported to descriptors. Same rules in the same order, so a note written
 * today names its target the way one written against the shadow tree did.
 */
function nameFromChain(chain: readonly ArtifactHitDescriptor[]): string {
  const self = chain[0];
  if (!self) return '';
  // An artifact can name its own regions with `data-element`, and when it does it outranks every
  // heuristic below — it is the author telling us what this is.
  if (self.data) return self.data;

  const { tag, text } = self;

  if (['path', 'circle', 'rect', 'line', 'g'].includes(tag)) {
    // The SDK walked out of the graphic to whatever contains the `<svg>`, and named THAT.
    const host = chain.slice(1).find((d) => d.tag !== 'svg');
    return host ? `graphic in ${nameFromChain([host])}` : 'graphic element';
  }
  if (tag === 'svg') {
    const parent = chain[1];
    if (parent?.tag === 'button') {
      return parent.text ? `icon in "${parent.text}" button` : 'button icon';
    }
    return 'icon';
  }
  if (tag === 'button') {
    if (self.aria) return `button [${self.aria}]`;
    return text ? `button "${text.slice(0, 25)}"` : 'button';
  }
  if (tag === 'a') {
    if (text) return `link "${text.slice(0, 25)}"`;
    if (self.href) return `link to ${self.href.slice(0, 30)}`;
    return 'link';
  }
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) {
    return text ? `${tag} "${text.slice(0, 35)}"` : tag;
  }
  if (tag === 'p') {
    return text ? `paragraph: "${text.slice(0, 40)}${text.length > 40 ? '...' : ''}"` : 'paragraph';
  }
  if (tag === 'span' || tag === 'label') {
    return text && text.length < 40 ? `"${text}"` : tag;
  }
  if (tag === 'li') {
    return text && text.length < 40 ? `list item: "${text.slice(0, 35)}"` : 'list item';
  }
  if (tag === 'blockquote') return 'blockquote';
  if (tag === 'code') return text && text.length < 30 ? `code: \`${text}\`` : 'code';
  if (tag === 'pre') return 'code block';
  if (tag === 'img') return self.alt ? `image "${self.alt.slice(0, 30)}"` : 'image';
  if (tag === 'video') return 'video';
  if (['div', 'section', 'article', 'nav', 'header', 'footer', 'aside', 'main'].includes(tag)) {
    if (self.aria) return `${tag} [${self.aria}]`;
    if (self.role) return self.role;
    if (self.cls) {
      const words = self.cls
        .split(/[\s_-]+/)
        .map((c) => c.replace(/[A-Z0-9]{5,}.*$/, ''))
        .filter((c) => c.length > 2 && !/^[a-z]{1,2}$/.test(c))
        .slice(0, 2);
      if (words.length > 0) return words.join(' ');
    }
    return tag === 'div' ? 'container' : tag;
  }
  return tag;
}

/* ── the composer popup (the SDK's AnnotationPopupCSS, ported) ─────────────────────────────────── */

interface NotePopupProps {
  /** Element name in the header. */
  header: string;
  placeholder: string;
  submitLabel: string;
  initialValue?: string;
  /** Submit button + focused-textarea border — accent for point notes, green for areas. */
  accent: string;
  /** Anchored above its point (flip near the bottom edge) — transform-origin swaps with it. */
  up: boolean;
  /** left + top|bottom, computed by the layer in natural units. */
  style: CSSProperties;
  /** Parent-driven exit (submit/delete paths) — the SDK's `isExiting` prop, same contract. */
  isExiting: boolean;
  /** Bumped on outside clicks — plays the SDK's shake instead of dismissing. */
  shakeNonce: number;
  onSubmit: (text: string) => void;
  /** Cancel path only: called AFTER the exit animation this popup plays itself. */
  onCancel: () => void;
  onDelete?: () => void;
}

function NotePopup({
  header,
  placeholder,
  submitLabel,
  initialValue = '',
  accent,
  up,
  style,
  isExiting,
  shakeNonce,
  onSubmit,
  onCancel,
  onDelete,
}: NotePopupProps) {
  const [text, setText] = useState(initialValue);
  const [animState, setAnimState] = useState<'initial' | 'enter' | 'entered' | 'exit'>('initial');
  const [isShaking, setIsShaking] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const timersRef = useRef<number[]>([]);
  const later = (fn: () => void, ms: number): void => {
    timersRef.current.push(window.setTimeout(fn, ms));
  };

  // The SDK's mount choreography: enter on the next tick, entered at 200ms (the animation length,
  // so hover/shake transforms can take over), caret into the textarea at 50ms, caret to the end.
  useEffect(() => {
    later(() => setAnimState('enter'), 0);
    later(() => setAnimState('entered'), 200);
    later(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
      textarea.scrollTop = textarea.scrollHeight;
    }, 50);
    const timers = timersRef.current;
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    if (isExiting) setAnimState('exit');
  }, [isExiting]);

  // Shake only on nonces bumped AFTER this popup opened — the counter is layer-wide and survives
  // between popups, so the mount value is the baseline, not zero.
  const shakeBaseline = useRef(shakeNonce);
  useEffect(() => {
    if (shakeNonce === shakeBaseline.current) return;
    setIsShaking(true);
    const t = window.setTimeout(() => {
      setIsShaking(false);
      textareaRef.current?.focus();
    }, 250);
    return () => window.clearTimeout(t);
  }, [shakeNonce]);

  const handleCancel = useCallback(() => {
    setAnimState('exit');
    later(onCancel, 150); // match the exit animation before the layer unmounts us
  }, [onCancel]);

  const handleSubmit = useCallback(() => {
    if (!text.trim()) return;
    onSubmit(text.trim());
  }, [text, onSubmit]);

  const className = [
    'd15a-popup',
    up ? 'd15a-up' : '',
    animState === 'enter' ? 'd15a-pop-enter' : '',
    animState === 'entered' ? 'd15a-pop-entered' : '',
    animState === 'exit' ? 'd15a-pop-exit' : '',
    isShaking ? 'd15a-pop-shake' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
  // POINTER SURFACE, deliberately: annotating is aiming at pixels, and the keyboard path is the
  // composer itself (Enter/Escape) plus the host's Annotate toggle — a key handler on the canvas
  // overlay would have no meaningful target to move between.
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={className}
      style={style}
      // The layer's mousedown is "outside click" — a click INSIDE the composer must not shake it.
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="d15a-pop-header">
        <span className="d15a-pop-element">{header}</span>
      </div>
      <textarea
        ref={textareaRef}
        className="d15a-pop-textarea"
        style={{ borderColor: isFocused ? accent : undefined }}
        placeholder={placeholder}
        value={text}
        rows={2}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.nativeEvent.isComposing) return;
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
          if (e.key === 'Escape') handleCancel();
        }}
      />
      <div className="d15a-pop-actions">
        {onDelete ? (
          <div className="d15a-pop-delete-wrap">
            <button type="button" className="d15a-pop-delete" onClick={onDelete}>
              <IconTrash size={22} />
            </button>
          </div>
        ) : null}
        <button type="button" className="d15a-pop-cancel" onClick={handleCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="d15a-pop-submit"
          style={{ backgroundColor: accent, opacity: text.trim() ? 1 : 0.4 }}
          disabled={!text.trim()}
          onClick={handleSubmit}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

/* ── the layer ─────────────────────────────────────────────────────────────────────────────────── */

/** A hover-highlighted element in natural coordinates, plus its identity for the note it may become. */
interface HoverHit {
  x: number;
  y: number;
  w: number;
  h: number;
  name: string;
  path: string;
  /** The hand-edited element's name tag under the hit, when there is one. */
  eid?: string;
}

/** A placed-but-unwritten note: where the composer is open, before Add commits it to the bus. */
interface PendingNote {
  x: number;
  y: number;
  w?: number;
  h?: number;
  /** The composer's anchor (release point = click point for point notes). */
  ax: number;
  ay: number;
  element?: string;
  elementPath?: string;
  eid?: string;
}

/** The SDK's own chrome counter-scale clamp — `Math.min(3, Math.max(1, z))`, verbatim. */
function zoomFor(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 1;
  return Math.min(3, Math.max(1, 1 / scale));
}

export function AnnotateLayer({ slideId, width, height, active, theme: themeProp }: AnnotateLayerProps) {
  const theme = useV15Theme(themeProp);
  useSyncExternalStore(subscribeAnnotations, annotationsVersion, annotationsServerVersion);
  const notes = listAnnotations(slideId);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [hover, setHover] = useState<HoverHit | null>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [pending, setPending] = useState<PendingNote | null>(null);
  const [pendingClosing, setPendingClosing] = useState(false);
  const [editing, setEditing] = useState<SlideAnnotation | null>(null);
  const [editingClosing, setEditingClosing] = useState(false);
  /**
   * WHICH MARK IS LIT — read off the bus, not held here.
   *
   * It has two sources and one of them is in another tree: the pointer entering this mark, and the
   * pointer entering that note's chip in the composer. Held locally, the second could never reach it.
   * See `focusAnnotation`.
   */
  const litId = focusedAnnotationId(slideId);
  const [shakeNonce, setShakeNonce] = useState(0);
  /** Marker ids whose enter animation already ran — `animation ... both` holds its last keyframe,
   *  which would beat the hover transform forever, so the class comes off on animationend (the
   *  SDK tracks the same `isAnimated` flag per annotation). */
  const [settled, setSettled] = useState<ReadonlySet<string>>(new Set());
  /** Removed notes ghosting through the SDK's red-X exit animation. */
  const [ghosts, setGhosts] = useState<SlideAnnotation[]>([]);

  const composing = pending != null || editing != null;

  useEffect(() => ensureAnnotateStyles(), []);

  // Any hop — mode off, another slide — drops every transient: the composer must never survive
  // onto a different artifact (the source's cross-slot leak class of bugs).
  useEffect(() => {
    setHover(null);
    setDrag(null);
    setPending(null);
    setPendingClosing(false);
    setEditing(null);
    setEditingClosing(false);
    setGhosts([]);
  }, [active, slideId]);

  /**
   * A chip in the composer was pressed: open that note's editor here.
   *
   * Keyed on the NONCE, not the id, so pressing the same chip twice in a row after a cancel opens it
   * both times. Guarded on the slide, because the request is session-wide and this layer draws one
   * artifact. Deliberately NOT guarded on `active`: the popup is reachable with the pen off — the
   * marks are — and a press in another pane should not require the user to arm a mode first.
   */
  const servedEdit = useRef(0);
  const editAsk = annotationEditRequest();
  useEffect(() => {
    if (!editAsk || editAsk.slideId !== slideId || editAsk.nonce === servedEdit.current) return;
    servedEdit.current = editAsk.nonce;
    const note = listAnnotations(slideId).find((n) => n.id === editAsk.id);
    if (note) setEditing(note);
  }, [editAsk, slideId]);

  /* ---- scale watch ----------------------------------------------------------------------------- */

  // The layer's LAYOUT box never changes (it is authored at natural size inside the scaled canvas),
  // so ResizeObserver is blind to zoom — only the on-screen rect moves. Poll it on the source
  // layer's own watchdog cadence (300ms) plus window resize; one getBoundingClientRect per tick,
  // only while edit mode is up.
  useEffect(() => {
    if (!active) return;
    const node = rootRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0) return;
      const next = zoomFor(rect.width / width);
      setZoom((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
    };
    measure();
    const tick = window.setInterval(measure, 300);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('resize', measure);
    };
  }, [active, width]);

  /* ---- pointing -------------------------------------------------------------------------------- */

  /** Screen → artifact coordinates, off the live rect (never a cached scale — mid-zoom clicks land). */
  const toNatural = useCallback(
    (clientX: number, clientY: number): { x: number; y: number; scale: number } => {
      const node = rootRef.current;
      if (!node) return { x: 0, y: 0, scale: 1 };
      const rect = node.getBoundingClientRect();
      const scale = rect.width > 0 ? rect.width / width : 1;
      return { x: (clientX - rect.left) / scale, y: (clientY - rect.top) / scale, scale };
    },
    [width],
  );

  /**
   * What is under this screen point, asked of the document itself.
   *
   * ASYNC, and that is the whole shape of the change: the answer comes back over `postMessage` from a
   * frame this origin cannot reach into, so it arrives a tick later instead of being read off the DOM.
   * Every caller therefore has to be able to survive its own answer being obsolete by the time it
   * lands, which is what the tokens below are for.
   *
   * The rect comes back in the artifact's OWN coordinates already — the frame's viewport is a hard
   * 1920x1080 that never scrolls, so client coordinates in there are artifact coordinates — and that is
   * the same space this layer draws in. Nothing has to be converted on the way back; only the point on
   * the way in.
   */
  const hitTest = useCallback(
    async (clientX: number, clientY: number): Promise<HoverHit | null> => {
      const point = toNatural(clientX, clientY);
      const hit = await hitTestArtifact(point.x, point.y);
      if (!hit) return null;
      return {
        x: hit.rect.x,
        y: hit.rect.y,
        w: hit.rect.w,
        h: hit.rect.h,
        name: nameFromChain(hit.chain),
        path: pathFromChain(hit.chain),
        eid: hit.eid ?? hit.chain.find((d) => d.eid)?.eid ?? undefined,
      };
    },
    [toNatural],
  );

  // Hover hit-tests ride rAF: one geometric walk per painted frame at most, always against the
  // LATEST pointer position (the ref, not the coords the frame was scheduled with).
  const hoverRaf = useRef<number | null>(null);
  const hoverPoint = useRef<{ clientX: number; clientY: number }>({ clientX: 0, clientY: 0 });
  /** Which hover answer is still wanted. See `queueHover`. */
  const hoverToken = useRef(0);
  useEffect(
    () => () => {
      if (hoverRaf.current != null) cancelAnimationFrame(hoverRaf.current);
    },
    [],
  );
  const queueHover = useCallback(
    (clientX: number, clientY: number) => {
      hoverPoint.current = { clientX, clientY };
      if (hoverRaf.current != null) return;
      hoverRaf.current = requestAnimationFrame(() => {
        hoverRaf.current = null;
        const { clientX: hx, clientY: hy } = hoverPoint.current;
        // The frame answers out of order under load, and a highlight drawn from an answer the pointer
        // has already moved past is worse than no highlight: it outlines something you are not on.
        const token = (hoverToken.current += 1);
        void hitTest(hx, hy).then((hit) => {
          if (hoverToken.current !== token) return;
          setHover(hit);
        });
      });
    },
    [hitTest],
  );

  // Mousedown book-keeping: where it went down (screen px, for the SDK's thresholds) and whether
  // it has travelled far enough to be a drag rather than a click.
  const downRef = useRef<{ cx: number; cy: number; moved: boolean } | null>(null);
  /** Which pending note a late naming answer is still allowed to patch. */
  const pendingToken = useRef(0);
  /**
   * Latest-value mirror, assigned in render — the pattern `DocumentEditor` uses for the same reason.
   *
   * The mouse-up handler has to seed the composer's header from the hover the pointer is on RIGHT NOW,
   * and hover changes on every animation frame the pointer moves. Listing it as a dependency would
   * rebuild this handler that often; reading it through a ref keeps the handler's identity stable and
   * its value fresh, which is what is actually wanted.
   */
  const hoverRef = useRef(hover);
  hoverRef.current = hover;

  const bumpShake = useCallback(() => setShakeNonce((n) => n + 1), []);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if (composing) {
        // The SDK shakes the open composer on outside clicks instead of dismissing it — a typed
        // half-note must not be lost to a stray click.
        bumpShake();
        return;
      }
      downRef.current = { cx: e.clientX, cy: e.clientY, moved: false };
    },
    [composing, bumpShake],
  );

  const handleMouseMove = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const down = downRef.current;
      if (down) {
        if (!down.moved && (Math.abs(e.clientX - down.cx) > 4 || Math.abs(e.clientY - down.cy) > 4)) {
          down.moved = true;
          setHover(null);
        }
        if (down.moved) {
          const a = toNatural(down.cx, down.cy);
          const b = toNatural(e.clientX, e.clientY);
          setDrag({ x0: a.x, y0: a.y, x1: b.x, y1: b.y });
        }
        return;
      }
      if (composing) return; // the SDK suppresses hover while a composer is open
      queueHover(e.clientX, e.clientY);
    },
    [composing, queueHover, toNatural],
  );

  const handleMouseUp = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const down = downRef.current;
      downRef.current = null;
      setDrag(null);
      if (!down || composing) return;

      if (down.moved) {
        // Drag release. The SDK creates an area note only for a meaningful rect (its own numbers:
        // > 20x20, screen px); anything smaller was a slip, not a selection.
        const sw = Math.abs(e.clientX - down.cx);
        const sh = Math.abs(e.clientY - down.cy);
        if (sw > 20 && sh > 20) {
          const a = toNatural(down.cx, down.cy);
          const b = toNatural(e.clientX, e.clientY);
          setPending({
            x: Math.min(a.x, b.x),
            y: Math.min(a.y, b.y),
            w: Math.abs(b.x - a.x),
            h: Math.abs(b.y - a.y),
            ax: b.x,
            ay: b.y,
          });
          setHover(null);
        }
        return;
      }

      // Plain click: a point note where it landed, wearing the element identity the document reports.
      //
      // The composer opens on the SAME frame as the click and the name is filled in behind it, rather
      // than the click waiting on a round trip to the frame — a caret that appears a tick after you
      // press is the one thing a note-taking surface may not do. In the ordinary case there is nothing
      // to see: the hover that preceded the click already named this element, so the header opens
      // correct and the patch below agrees with it. `hover` is read before it is cleared.
      const p = toNatural(e.clientX, e.clientY);
      const named = hoverRef.current;
      setPending({ x: p.x, y: p.y, ax: p.x, ay: p.y, element: named?.name, elementPath: named?.path, eid: named?.eid });
      setHover(null);
      const token = (pendingToken.current += 1);
      void hitTest(e.clientX, e.clientY).then((hit) => {
        // Cancelled, submitted, or superseded by another click while the frame was answering.
        if (!hit || pendingToken.current !== token) return;
        setPending((cur) => (cur ? { ...cur, element: hit.name, elementPath: hit.path, eid: hit.eid } : cur));
      });
    },
    [composing, hitTest, toNatural],
  );

  const handleMouseLeave = useCallback(() => {
    downRef.current = null;
    setHover(null);
    setDrag(null);
  }, []);

  /* ---- composer outcomes ----------------------------------------------------------------------- */

  const timersRef = useRef<number[]>([]);
  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, []);
  const later = useCallback((fn: () => void, ms: number) => {
    timersRef.current.push(window.setTimeout(fn, ms));
  }, []);

  /** Parent-driven close (submit/delete): flag → popup plays exit → state drops at 150ms, the
   *  SDK's exit duration. Cancel closes itself and lands in the plain clear. */
  const closePending = useCallback(() => {
    setPendingClosing(true);
    later(() => {
      setPending(null);
      setPendingClosing(false);
    }, 150);
  }, [later]);

  const closeEditing = useCallback(() => {
    setEditingClosing(true);
    later(() => {
      setEditing(null);
      setEditingClosing(false);
    }, 150);
  }, [later]);

  const ghost = useCallback(
    (note: SlideAnnotation) => {
      setGhosts((g) => [...g, note]);
      later(() => setGhosts((g) => g.filter((x) => x.id !== note.id)), 220);
    },
    [later],
  );

  const submitPending = useCallback(
    (text: string) => {
      if (!pending) return;
      addAnnotation(slideId, {
        x: pending.x,
        y: pending.y,
        ...(pending.w != null && pending.h != null ? { w: pending.w, h: pending.h } : null),
        text,
        element: pending.element,
        elementPath: pending.elementPath,
        ...(pending.eid ? { eid: pending.eid } : null),
      });
      closePending();
    },
    [pending, slideId, closePending],
  );

  const submitEditing = useCallback(
    (text: string) => {
      if (!editing) return;
      updateAnnotation(slideId, editing.id, { text });
      closeEditing();
    },
    [editing, slideId, closeEditing],
  );

  const deleteEditing = useCallback(() => {
    if (!editing) return;
    removeAnnotation(slideId, editing.id);
    ghost(editing);
    closeEditing();
  }, [editing, slideId, ghost, closeEditing]);

  /* ---- render ---------------------------------------------------------------------------------- */

  /**
   * WHAT SURVIVES `active` GOING FALSE — a deliberate break with the source (deviation 5), whose Done
   * chip unmounted the whole layer.
   *
   * It unmounted the MARKS with it, and that was a lie about the state: the notes were still staged and
   * still riding the next message, but the artifact stopped admitting it. So the split is now between
   * the SURFACE and the MARKS. Switching the pen off retires the crosshair, the drag-select and the
   * hover highlight — the layer stops taking the pointer at all, so the artifact underneath is live
   * again — while every staged note keeps its mark, still hoverable and still openable.
   *
   * Nothing to draw and no surface to draw it on is the one case that still renders nothing.
   * All hooks are above this line, so the early returns are legal.
   */
  if (width <= 0 || height <= 0) return null;
  if (!active && notes.length === 0) return null;

  /** Composer geometry — the SDK's fixed-position rules mapped onto the artifact box: 280px wide
   *  (screen), clamped 20px inside the edges, flipped above its anchor within 290px of the bottom.
   *  All in natural units via the zoom factor, because the artifact box IS the viewport here (the
   *  frame clips at its edge). */
  const popupPlacement = (ax: number, ay: number): { style: CSSProperties; up: boolean } => {
    const left = Math.max(160 * zoom, Math.min(width - 160 * zoom, ax));
    const up = ay > height - 290 * zoom;
    return up
      ? { style: { left, bottom: height - ay + 20 * zoom }, up: true }
      : { style: { left, top: ay + 20 * zoom }, up: false };
  };

  const markerFor = (note: SlideAnnotation, index: number): ReactNode => {
    const isArea = note.w != null && note.h != null;
    // The bus stores a rect as x/y/w/h with no separate anchor, so an area's marker sits at the
    // rect's center (the SDK put its group badge there too); point markers sit on their point.
    const cx = isArea ? note.x + (note.w ?? 0) / 2 : note.x;
    const cy = isArea ? note.y + (note.h ?? 0) / 2 : note.y;
    const hovered = litId === note.id && !composing;
    const className = [
      'd15a-marker',
      isArea ? 'd15a-area' : '',
      settled.has(note.id) ? '' : 'd15a-enter',
      // Lit from the tray. Only while the mark is settled: the entrance animation holds its own
      // last keyframe and would beat the transform anyway, and a mark arriving already grown reads
      // as a glitch rather than as an answer.
      litId === note.id && settled.has(note.id) ? 'd15a-lit' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      // POINTER SURFACE, deliberately: annotating is aiming at pixels, and the keyboard path is the
  // composer itself (Enter/Escape) plus the host's Annotate toggle — a key handler on the canvas
  // overlay would have no meaningful target to move between.
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        key={note.id}
        className={className}
        data-annotation-marker
        // The SDK's stagger: entering markers land 20ms apart (visible when edit mode re-opens on
        // an already-annotated slide and the whole set replays its entrance).
        style={{ left: cx, top: cy, animationDelay: settled.has(note.id) ? undefined : `${index * 20}ms` }}
        onAnimationEnd={() => setSettled((s) => (s.has(note.id) ? s : new Set(s).add(note.id)))}
        onMouseEnter={() => focusAnnotation(slideId, note.id)}
        onMouseLeave={() => focusAnnotation(slideId, null)}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (composing) {
            bumpShake();
            return;
          }
          setHover(null);
          setEditing(note);
        }}
      >
        {/* markerClickBehavior "edit" (the SDK default): hover trades the number for the pencil. */}
        {hovered ? <IconEdit size={16} /> : <span>{index + 1}</span>}
        {hovered ? (
          <div className="d15a-tip">
            <span className="d15a-tip-quote">{annotationName(note)}</span>
            <span className="d15a-tip-note">{note.text}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const pendingIsArea = pending?.w != null && pending?.h != null;

  return (
  // POINTER SURFACE, deliberately: annotating is aiming at pixels, and the keyboard path is the
  // composer itself (Enter/Escape) plus the host's Annotate toggle — a key handler on the canvas
  // overlay would have no meaningful target to move between.
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={rootRef}
      className={`d15a-layer${theme === 'light' ? ' d15a-light' : ''}${active ? '' : ' d15a-idle'}`}
      style={{ width, height, ['--d15a-zoom' as string]: zoom } as CSSProperties}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hover highlight — suppressed while composing or dragging, like the SDK, and absent entirely
          once the pen is off: it is the surface answering the pointer, and there is no surface. */}
      {active && hover && !composing && !drag ? (
        <div
          className="d15a-hover"
          style={{ left: hover.x, top: hover.y, width: hover.w, height: hover.h }}
        />
      ) : null}

      {/* Drag-select rect (green — the SDK's area affordance). */}
      {drag ? (
        <div
          className="d15a-drag"
          style={{
            left: Math.min(drag.x0, drag.x1),
            top: Math.min(drag.y0, drag.y1),
            width: Math.abs(drag.x1 - drag.x0),
            height: Math.abs(drag.y1 - drag.y0),
          }}
        />
      ) : null}

      {/* Hovering an area's marker reveals its region — the SDK's hover-reveal for group notes. */}
      {notes.map((note) =>
        note.w != null && note.h != null && litId === note.id && !composing ? (
          <div
            key={`region-${note.id}`}
            className="d15a-region"
            style={{ left: note.x, top: note.y, width: note.w, height: note.h }}
          />
        ) : null,
      )}

      {notes.map(markerFor)}

      {/* Deleted notes ghost out through the SDK's red-X exit. */}
      {ghosts.map((note) => {
        const isArea = note.w != null && note.h != null;
        const cx = isArea ? note.x + (note.w ?? 0) / 2 : note.x;
        const cy = isArea ? note.y + (note.h ?? 0) / 2 : note.y;
        return (
          <div
            key={`ghost-${note.id}`}
            className={`d15a-marker d15a-del d15a-exit${isArea ? ' d15a-area' : ''}`}
            style={{ left: cx, top: cy }}
          >
            <IconXmark size={isArea ? 12 : 10} />
          </div>
        );
      })}

      {/* The pending "+" marker at the composer's anchor, until Add trades it for a numbered one. */}
      {pending && !pendingClosing ? (
        // POINTER SURFACE, deliberately: annotating is aiming at pixels, and the keyboard path is the
        // composer itself (Enter/Escape) plus the host's Annotate toggle — a key handler on the canvas
        // overlay would have no meaningful target to move between.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={`d15a-marker d15a-pending d15a-enter${pendingIsArea ? ' d15a-area' : ''}`}
          style={{ left: pending.ax, top: pending.ay }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <IconPlus size={12} />
        </div>
      ) : null}

      {pending
        ? (() => {
            const placed = popupPlacement(pending.ax, pending.ay);
            return (
              <NotePopup
                header={annotationName(pending)}
                // The SDK's own composer strings, verbatim.
                placeholder={pendingIsArea ? 'What should change in this area?' : 'What should change?'}
                submitLabel="Add"
                accent={pendingIsArea ? 'var(--d15a-green)' : 'var(--d15a-accent)'}
                up={placed.up}
                style={placed.style}
                isExiting={pendingClosing}
                shakeNonce={shakeNonce}
                onSubmit={submitPending}
                onCancel={() => {
                  setPending(null);
                  setPendingClosing(false);
                }}
              />
            );
          })()
        : null}

      {editing
        ? (() => {
            const isArea = editing.w != null && editing.h != null;
            const ax = isArea ? editing.x + (editing.w ?? 0) / 2 : editing.x;
            const ay = isArea ? editing.y + (editing.h ?? 0) / 2 : editing.y;
            const placed = popupPlacement(ax, ay);
            return (
              <NotePopup
                header={annotationName(editing)}
                placeholder="Edit your feedback..."
                submitLabel="Save"
                initialValue={editing.text}
                accent={isArea ? 'var(--d15a-green)' : 'var(--d15a-accent)'}
                up={placed.up}
                style={placed.style}
                isExiting={editingClosing}
                shakeNonce={shakeNonce}
                onSubmit={submitEditing}
                onCancel={() => {
                  setEditing(null);
                  setEditingClosing(false);
                }}
                onDelete={deleteEditing}
              />
            );
          })()
        : null}
    </div>
  );
}
